import { extendTheme } from '@chakra-ui/react';

const theme = extendTheme({
    config: {
        initialColorMode: 'light',
        useSystemColorMode: false,
    },
    styles: {
        global: {
            body: {
                bg: '#faf9f5',
                color: '#141413',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                lineHeight: 1.55,
                letterSpacing: 0,
                fontSize: '16px',
            },
            '::selection': {
                bg: '#cc785c',
                color: '#ffffff',
            },
            '::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
            },
            '::-webkit-scrollbar-track': {
                bg: '#ebe6df',
            },
            '::-webkit-scrollbar-thumb': {
                bg: '#8e8b82',
                borderRadius: '6px',
            },
            '::-webkit-scrollbar-thumb:hover': {
                bg: '#6c6a64',
            },
        },
    },
    colors: {
        brand: {
            primary: '#cc785c',
            primaryActive: '#a9583e',
            primaryDisabled: '#e6dfd8',
        },
        canvas: '#faf9f5',
        ink: '#141413',
        body: '#3d3d3a',
        bodyStrong: '#252523',
        muted: '#6c6a64',
        mutedSoft: '#8e8b82',
        hairline: '#e6dfd8',
        hairlineSoft: '#ebe6df',
        surfaceSoft: '#f5f0e8',
        surfaceCard: '#efe9de',
        surfaceCreamStrong: '#e8e0d2',
        surfaceDark: '#181715',
        surfaceDarkElevated: '#252320',
        surfaceDarkSoft: '#1f1e1b',
        onPrimary: '#ffffff',
        onDark: '#faf9f5',
        onDarkSoft: '#a09d96',
        accentTeal: '#5db8a6',
        accentAmber: '#e8a55a',
        success: '#5db872',
        warning: '#d4a017',
        error: '#c64545',
        teal: {
            50: '#f0fdf9',
            100: '#ccfbf1',
            200: '#99f6e4',
            300: '#5db8a6',
            400: '#5db8a6',
            500: '#5db8a6',
            600: '#4a9a88',
            700: '#3d7d70',
            800: '#2d5f55',
            900: '#1e403a',
        },
    },
    fonts: {
        heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        mono: '"JetBrains Mono", ui-monospace, monospace',
    },
    fontSizes: {
        xs: '13px',
        sm: '14px',
        md: '16px',
        lg: '18px',
        xl: '22px',
        '2xl': '28px',
        '3xl': '36px',
        '4xl': '48px',
        '5xl': '64px',
    },
    fontWeights: {
        normal: 400,
        medium: 500,
        bold: 700,
    },
    lineHeights: {
        tighter: 1.05,
        tight: 1.1,
        snug: 1.15,
        normal: 1.2,
        relaxed: 1.3,
        loose: 1.4,
        base: 1.55,
        code: 1.6,
    },
    letterSpacings: {
        tighter: '-1.5px',
        tight: '-1px',
        snug: '-0.5px',
        normal: '-0.3px',
        wide: '1.5px',
    },
    space: {
        xxs: '4px',
        xs: '8px',
        sm: '12px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
        section: '96px',
    },
    radii: {
        xs: '4px',
        sm: '6px',
        md: '8px',
        lg: '12px',
        xl: '16px',
        pill: '9999px',
        full: '9999px',
    },
    shadows: {
        subtle: '0 1px 3px rgba(20,20,19,0.08)',
        raised: '0 4px 12px rgba(20,20,19,0.1)',
    },
    components: {
        Button: {
            baseStyle: {
                fontWeight: 500,
                lineHeight: 1,
                borderRadius: 'md',
                transition: 'all 0.2s ease',
            },
            sizes: {
                md: {
                    h: '40px',
                    px: '20px',
                    fontSize: '14px',
                },
                lg: {
                    h: '48px',
                    px: '24px',
                    fontSize: '16px',
                },
                sm: {
                    h: '32px',
                    px: '12px',
                    fontSize: '13px',
                },
            },
            variants: {
                primary: {
                    bg: 'brand.primary',
                    color: 'onPrimary',
                    _hover: {
                        bg: 'brand.primaryActive',
                        _disabled: {
                            bg: 'brand.primaryDisabled',
                        },
                    },
                    _active: {
                        bg: 'brand.primaryActive',
                    },
                    _disabled: {
                        bg: 'brand.primaryDisabled',
                        color: 'muted',
                    },
                },
                secondary: {
                    bg: 'canvas',
                    color: 'ink',
                    border: '1px solid',
                    borderColor: 'hairline',
                    _hover: {
                        bg: 'surfaceSoft',
                    },
                },
                secondaryDark: {
                    bg: 'surfaceDarkElevated',
                    color: 'onDark',
                    _hover: {
                        bg: 'surfaceDarkSoft',
                    },
                },
                solid: {
                    bg: 'brand.primary',
                    color: 'onPrimary',
                    _hover: {
                        bg: 'brand.primaryActive',
                    },
                },
                outline: {
                    borderColor: 'hairline',
                    color: 'ink',
                    _hover: {
                        bg: 'surfaceSoft',
                    },
                },
                ghost: {
                    color: 'ink',
                    _hover: {
                        bg: 'surfaceSoft',
                    },
                },
                link: {
                    color: 'ink',
                    _hover: {
                        textDecoration: 'none',
                        bg: 'surfaceSoft',
                    },
                },
                teal: {
                    bg: 'teal.500',
                    color: 'white',
                    _hover: {
                        bg: 'teal.600',
                    },
                },
            },
            defaultProps: {
                variant: 'primary',
            },
        },
        Input: {
            variants: {
                outline: {
                    field: {
                        bg: 'canvas',
                        color: 'ink',
                        border: '1px solid',
                        borderColor: 'hairline',
                        borderRadius: 'md',
                        h: '40px',
                        px: '14px',
                        fontSize: 'md',
                        _focus: {
                            borderColor: 'brand.primary',
                            boxShadow: '0 0 0 3px rgba(204, 120, 92, 0.15)',
                        },
                        _placeholder: {
                            color: 'mutedSoft',
                        },
                    },
                },
                filled: {
                    field: {
                        bg: 'surfaceCard',
                        color: 'ink',
                        borderRadius: 'md',
                        _focus: {
                            bg: 'canvas',
                            borderColor: 'brand.primary',
                        },
                    },
                },
            },
            defaultProps: {
                variant: 'outline',
            },
        },
        Link: {
            baseStyle: {
                color: 'brand.primary',
                fontWeight: 500,
                _hover: {
                    textDecoration: 'underline',
                },
            },
        },
        Heading: {
            baseStyle: {
                fontWeight: 400,
                letterSpacing: '-0.5px',
                color: 'ink',
            },
            sizes: {
                '5xl': {
                    fontSize: '64px',
                    lineHeight: '1.05',
                    letterSpacing: '-1.5px',
                },
                '4xl': {
                    fontSize: '48px',
                    lineHeight: '1.1',
                    letterSpacing: '-1px',
                },
                '3xl': {
                    fontSize: '36px',
                    lineHeight: '1.15',
                    letterSpacing: '-0.5px',
                },
                '2xl': {
                    fontSize: '28px',
                    lineHeight: '1.2',
                    letterSpacing: '-0.3px',
                },
                xl: {
                    fontSize: '22px',
                    lineHeight: '1.3',
                    fontWeight: 500,
                },
                lg: {
                    fontSize: '18px',
                    lineHeight: '1.4',
                    fontWeight: 500,
                },
                md: {
                    fontSize: '16px',
                    lineHeight: '1.4',
                    fontWeight: 500,
                },
            },
        },
        Text: {
            baseStyle: {
                color: 'body',
                lineHeight: 'base',
            },
            variants: {
                muted: {
                    color: 'muted',
                },
                strong: {
                    color: 'bodyStrong',
                    fontWeight: 500,
                },
                caption: {
                    fontSize: '13px',
                    fontWeight: 500,
                },
            },
        },
        Box: {
            baseStyle: {
                borderRadius: 'md',
            },
        },
        Card: {
            baseStyle: {
                container: {
                    bg: 'surfaceCard',
                    borderRadius: 'lg',
                    p: '32px',
                },
            },
        },
        CardDark: {
            baseStyle: {
                container: {
                    bg: 'surfaceDark',
                    color: 'onDark',
                    borderRadius: 'lg',
                    p: '32px',
                },
            },
        },
        Badge: {
            variants: {
                pill: {
                    bg: 'surfaceCard',
                    color: 'ink',
                    fontSize: '13px',
                    fontWeight: 500,
                    borderRadius: 'pill',
                    px: '12px',
                    py: '4px',
                },
                coral: {
                    bg: 'brand.primary',
                    color: 'onPrimary',
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    borderRadius: 'pill',
                    px: '12px',
                    py: '4px',
                },
            },
        },
        Menu: {
            baseStyle: {
                list: {
                    bg: 'canvas',
                    border: '1px solid',
                    borderColor: 'hairline',
                    borderRadius: 'lg',
                    py: '8px',
                    boxShadow: 'raised',
                },
                item: {
                    bg: 'transparent',
                    color: 'ink',
                    fontWeight: 500,
                    _hover: {
                        bg: 'surfaceSoft',
                    },
                    _focus: {
                        bg: 'surfaceSoft',
                    },
                },
            },
        },
        Modal: {
            baseStyle: {
                overlay: {
                    bg: 'rgba(0, 0, 0, 0.6)',
                },
                dialog: {
                    bg: 'canvas',
                    borderRadius: 'lg',
                    border: '1px solid',
                    borderColor: 'hairline',
                },
                header: {
                    color: 'ink',
                    fontWeight: 500,
                },
                body: {
                    color: 'body',
                },
            },
        },
        Divider: {
            baseStyle: {
                borderColor: 'hairline',
            },
        },
        Breadcrumb: {
            baseStyle: {
                color: 'muted',
                fontSize: '14px',
            },
        },
        Avatar: {
            baseStyle: {
                container: {
                    borderRadius: 'full',
                },
            },
        },
        FormLabel: {
            baseStyle: {
                color: 'bodyStrong',
                fontWeight: 500,
                fontSize: '14px',
            },
        },
    },
});

export default theme;